import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import { Upload, CheckCircle, BookOpen, Info, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Progress } from './components/ui/progress';
import { Badge } from './components/ui/badge';
import {
  PLATFORM_INFO, PLATFORM_PARSERS, detectCsvFormat, findExistingBook,
} from './lib/csvImportUtils';
import { resolveBookTags } from './lib/bookTaxonomy';
import {
  setDoc, doc, getDoc, getDocs, updateDoc, addDoc, deleteDoc,
  collection, increment, serverTimestamp, query, where, writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

/* ═══════════════════════════════════════════
   READING HISTORY IMPORT
   Multi-platform import for both admin and reader portal.
   Writes to bookDatabase (shared) AND readers/{id}/readingHistory (personal).
═══════════════════════════════════════════ */

const PLATFORMS = Object.keys(PLATFORM_INFO);

function ExportInstructions({ platform }) {
  const [open, setOpen] = useState(false);
  const info = PLATFORM_INFO[platform];
  if (!info) return null;

  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-teal-600">
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-6 z-20 w-64 rounded-lg border border-slate-200 bg-white p-3 shadow-lg text-left">
          <p className="text-xs font-bold text-slate-700 mb-2">How to export from {info.name}:</p>
          <ol className="text-[11px] text-slate-600 space-y-1 list-decimal pl-4">
            {info.exportSteps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
          <button onClick={() => setOpen(false)} className="mt-2 text-[10px] text-teal-600 hover:underline">Close</button>
        </div>
      )}
    </div>
  );
}

export default function ReadingHistoryImport({
  readerData,
  readerId: propReaderId,
  existingBooks,
  onComplete,
  isAdmin = false,
  allReaders = [],
  adminUserId = null,
}) {
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [adminReaderId, setAdminReaderId] = useState('');
  const [importBatches, setImportBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const fileRef = useRef(null);

  const readerId = isAdmin ? adminReaderId : (propReaderId || readerData?.id);

  const loadImportBatches = async (rid) => {
    if (!rid) return;
    try {
      const snap = await getDocs(collection(db, 'readers', rid, 'importBatches'));
      const batches = [];
      snap.forEach(d => batches.push({ id: d.id, ...d.data() }));
      batches.sort((a, b) => (b.importedAt?.seconds || 0) - (a.importedAt?.seconds || 0));
      setImportBatches(batches);
    } catch (e) {
      console.error('[ReadingHistoryImport] Failed to load batches:', e);
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        if (!results.data?.length) { alert('CSV is empty'); return; }
        const cols = Object.keys(results.data[0] || {});
        const format = detectCsvFormat(cols);
        setDetectedFormat(format);
        setCsvData(results.data);
        setResult(null);

        if (format === 'unknown') return;

        const parser = PLATFORM_PARSERS[format];
        if (!parser) return;

        let matched = 0, newBooks = 0, skipped = 0;
        const parsed = [];

        for (const row of results.data) {
          const book = parser(row);
          if (!book.title) { skipped++; continue; }
          const existing = existingBooks ? findExistingBook(book, existingBooks) : null;
          parsed.push({ ...book, existingMatch: existing });
          if (existing) matched++;
          else newBooks++;
        }

        setCsvPreview({ matched, newBooks, skipped, total: results.data.length, parsed });
      },
      error: () => alert('Failed to parse CSV'),
    });
    if (e.target) e.target.value = '';
  };

  const handleImport = async () => {
    if (!csvPreview?.parsed || !readerId) {
      console.error('[ReadingHistoryImport] Cannot import: missing parsed data or readerId', { hasParsed: !!csvPreview?.parsed, readerId });
      return;
    }
    console.log(`[ReadingHistoryImport] Starting import: ${csvPreview.parsed.length} books for reader ${readerId}`);
    setImporting(true);
    setImportProgress(0);

    const batchId = `${readerId}_${Date.now()}`;
    let added = 0, matched = 0, errors = 0, pointsEarned = 0, shelfWrites = 0;
    const total = csvPreview.parsed.length;
    const BATCH_SIZE = 500;
    let firestoreBatch = writeBatch(db);
    let batchCount = 0;

    const commitBatchIfNeeded = async () => {
      if (batchCount >= BATCH_SIZE) {
        await firestoreBatch.commit();
        firestoreBatch = writeBatch(db);
        batchCount = 0;
      }
    };

    // Google Books fetch with exponential backoff (handles rate limiting)
    const fetchGoogleBooks = async (queryStr, retries = 3) => {
      for (let attempt = 0; attempt < retries; attempt++) {
        try {
          const resp = await fetch(
            `https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(queryStr)}&maxResults=1`
          );
          if (resp.status === 429) {
            // Rate limited — wait with exponential backoff
            const delay = Math.pow(2, attempt) * 1000 + Math.random() * 500;
            console.warn(`[ReadingHistoryImport] Rate limited, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${retries})`);
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          if (!resp.ok) {
            console.warn(`[ReadingHistoryImport] Google Books API error ${resp.status}`);
            return null;
          }
          const data = await resp.json();
          return data.items?.length ? data.items[0] : null;
        } catch (e) {
          console.warn(`[ReadingHistoryImport] Google Books fetch error (attempt ${attempt + 1}):`, e.message);
          if (attempt < retries - 1) {
            await new Promise(r => setTimeout(r, Math.pow(2, attempt) * 1000));
          }
        }
      }
      return null;
    };

    for (let i = 0; i < total; i++) {
      const book = csvPreview.parsed[i];
      setImportProgress(Math.round(((i + 1) / total) * 100));

      try {
        let bookId = null;

        if (book.existingMatch) {
          bookId = book.existingMatch.id || book.existingMatch.googleBooksId;
          matched++;
          // Add reader to contributors
          if (readerId && bookId) {
            try {
              const existDoc = await getDoc(doc(db, 'bookDatabase', bookId));
              if (existDoc.exists()) {
                const contributors = existDoc.data().contributors || [];
                if (!contributors.includes(readerId)) {
                  await updateDoc(doc(db, 'bookDatabase', bookId), {
                    contributors: [...contributors, readerId],
                  });
                }
              }
            } catch (e) { /* contributor update is non-critical */ }
          }
        } else {
          // Look up on Google Books — try ISBN first, then title+author
          let item = null;
          if (book.isbn) {
            item = await fetchGoogleBooks(`isbn:${book.isbn}`);
          }
          if (!item) {
            // Fall back to title+author search
            const titleQuery = `intitle:${book.title}${book.author ? `+inauthor:${book.author}` : ''}`;
            item = await fetchGoogleBooks(titleQuery);
          }

          if (item) {
            // Google Books found a match
            const vol = item.volumeInfo;
            bookId = item.id;

            const existingDoc = await getDoc(doc(db, 'bookDatabase', bookId));
            if (existingDoc.exists()) {
              matched++;
              // Add reader to contributors if not already there
              if (readerId) {
                const existingData = existingDoc.data();
                const contributors = existingData.contributors || [];
                if (!contributors.includes(readerId)) {
                  await updateDoc(doc(db, 'bookDatabase', bookId), {
                    contributors: [...contributors, readerId],
                  });
                }
              }
            } else {
              await setDoc(doc(db, 'bookDatabase', bookId), {
                title: vol.title || book.title,
                author: (vol.authors || []).join(', ') || book.author,
                googleBooksId: bookId,
                isbn: book.isbn || (vol.industryIdentifiers || []).find(id => id.type === 'ISBN_13')?.identifier || null,
                cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
                description: (vol.description || '').substring(0, 300),
                pageCount: vol.pageCount || book.pages || null,
                publishedDate: vol.publishedDate || null,
                googleBooksLink: vol.infoLink || null,
                autoTags: book.autoTags,
                adminTags: {},
                resolvedTags: {},
                communityTags: {},
                contributors: readerId ? [readerId] : [],
                submissionCount: 0,
                needsTagging: true,
                dateAdded: new Date().toISOString(),
                addedBy: isAdmin ? 'admin-import' : 'reader-import',
              });
              added++;
              if (!isAdmin) pointsEarned += 2;
            }
          } else {
            // Google Books couldn't find it — save with CSV data only (no cover)
            bookId = `csv_${(book.title + '_' + (book.author || '')).replace(/[^a-zA-Z0-9]/g, '_').substring(0, 80)}_${Date.now()}`;

            // Check if we already have a similar entry in bookDatabase
            const existingDoc = await getDoc(doc(db, 'bookDatabase', bookId));
            if (!existingDoc.exists()) {
              await setDoc(doc(db, 'bookDatabase', bookId), {
                title: book.title,
                author: book.author || 'Unknown',
                googleBooksId: null,
                isbn: book.isbn || null,
                cover: null,
                description: '',
                pageCount: book.pages || null,
                publishedDate: null,
                googleBooksLink: null,
                autoTags: book.autoTags || {},
                adminTags: {},
                resolvedTags: {},
                communityTags: {},
                contributors: readerId ? [readerId] : [],
                submissionCount: 0,
                needsTagging: true,
                dateAdded: new Date().toISOString(),
                addedBy: isAdmin ? 'admin-import' : 'reader-import',
                importSource: 'csv-only',
              });
              added++;
              if (!isAdmin) pointsEarned += 2;
            } else {
              matched++;
            }
          }
        }

        // Save to reader's personal reading history (bookshelf)
        if (bookId && readerId) {
          try {
            const histRef = doc(db, 'readers', readerId, 'readingHistory', bookId);
            const existingHist = await getDoc(histRef);
            const histData = {
              bookId,
              title: book.title,
              author: book.author,
              rating: book.rating || null,
              dateRead: book.dateRead || null,
              readStatus: book.readStatus || 'read',
              source: book.source,
              importBatchId: batchId,
              importedAt: serverTimestamp(),
              importedBy: isAdmin ? 'admin' : 'self',
              ...(isAdmin && adminUserId ? { adminId: adminUserId } : {}),
              readerId: readerId,
            };
            if (existingHist.exists()) {
              await updateDoc(histRef, histData);
            } else {
              await setDoc(histRef, histData);
            }
            shelfWrites++;
          } catch (histErr) {
            console.error(`[ReadingHistoryImport] Failed to write readingHistory for "${book.title}":`, histErr.code, histErr.message);
          }
        }

        // Submit reader's tags as community submission (reader portal only)
        if (!isAdmin && bookId && Object.keys(book.autoTags).length > 0 && readerData?.id) {
          const subRef = doc(db, 'bookDatabase', bookId, 'submissions', readerData.id);
          const existingSub = await getDoc(subRef);

          if (!existingSub.exists()) {
            const submission = {
              submittedAt: new Date().toISOString(),
              readerName: readerData.name || readerData.emailCorrespondence || readerData.id,
              id: readerData.id,
            };
            for (const [secKey, items] of Object.entries(book.autoTags)) {
              submission[secKey] = items;
            }
            await setDoc(subRef, submission);

            const subsSnap = await getDocs(collection(db, 'bookDatabase', bookId, 'submissions'));
            const subs = [];
            subsSnap.forEach(d => subs.push({ id: d.id, ...d.data() }));

            const bookDoc = await getDoc(doc(db, 'bookDatabase', bookId));
            const adminTags = bookDoc.exists() ? (bookDoc.data().adminTags || {}) : {};
            const resolved = resolveBookTags(adminTags, subs);

            let causedVerification = false;
            for (const secKey of Object.keys(book.autoTags)) {
              for (const [item, info] of Object.entries(resolved[secKey] || {})) {
                if (info.source === 'community' && info.count >= 3) {
                  causedVerification = true;
                }
              }
            }

            await updateDoc(doc(db, 'bookDatabase', bookId), {
              resolvedTags: resolved,
              submissionCount: subs.length,
            });

            if (causedVerification) pointsEarned += 5;
          }
        }

        // Rate limit
        await new Promise(r => setTimeout(r, 80));
      } catch (e) {
        console.error(`[ReadingHistoryImport] Error for "${book.title}":`, e.message);
        errors++;
      }
    }

    console.log(`[ReadingHistoryImport] Import complete: ${added} new, ${matched} matched, ${errors} errors, ${shelfWrites} shelf writes`);

    // Save import batch record
    if (readerId) {
      try {
        await setDoc(doc(db, 'readers', readerId, 'importBatches', batchId), {
          batchId,
          platform: detectedFormat,
          booksImported: added + matched,
          newBooksAdded: added,
          matchedExisting: matched,
          errors,
          pointsEarned,
          importedAt: serverTimestamp(),
        });
      } catch (e) {
        console.error('[ReadingHistoryImport] Batch record error:', e);
      }
    }

    // Award points (reader portal only)
    if (!isAdmin && pointsEarned > 0 && readerData?.id) {
      try {
        await updateDoc(doc(db, 'readers', readerData.id), { totalPoints: increment(pointsEarned) });
        await addDoc(collection(db, 'pointsHistory'), {
          readerId: readerData.id,
          amount: pointsEarned,
          type: 'csv_import',
          reason: `Imported ${added + matched} books from ${PLATFORM_INFO[detectedFormat]?.name || detectedFormat} (${added} new, ${matched} existing)`,
          createdAt: serverTimestamp(),
          createdBy: 'system',
        });
      } catch (e) {
        console.error('[ReadingHistoryImport] Points error:', e);
      }
    }

    setResult({ added, matched, errors, pointsEarned, batchId, shelfWrites });
    setImporting(false);
    if (readerId) loadImportBatches(readerId);
    if (onComplete) onComplete({ added, matched, shelfWrites });
  };

  const handleDeleteBatch = async (batch) => {
    if (!readerId || !batch.batchId) return;
    setDeletingBatch(batch.batchId);
    try {
      // Delete all readingHistory docs with this batchId
      const histSnap = await getDocs(
        query(collection(db, 'readers', readerId, 'readingHistory'), where('importBatchId', '==', batch.batchId))
      );
      const delBatch = writeBatch(db);
      let count = 0;
      histSnap.forEach(d => { delBatch.delete(d.ref); count++; });
      // Delete the batch record
      delBatch.delete(doc(db, 'readers', readerId, 'importBatches', batch.batchId));
      await delBatch.commit();
      setImportBatches(prev => prev.filter(b => b.batchId !== batch.batchId));
    } catch (e) {
      console.error('[ReadingHistoryImport] Delete batch error:', e);
      alert('Failed to delete import batch');
    }
    setDeletingBatch(null);
  };

  const reset = () => {
    setCsvData(null);
    setCsvPreview(null);
    setResult(null);
    setDetectedFormat(null);
    setSelectedPlatform(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-1">Import Reading History</h3>
        <p className="text-sm text-slate-500">
          Upload your reading history from any supported platform.
          {!isAdmin && ' Earn points for new books and verified tags.'}
        </p>
      </div>

      {/* Admin reader selector */}
      {isAdmin && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Import for reader:</label>
          <select
            value={adminReaderId}
            onChange={(e) => { setAdminReaderId(e.target.value); if (e.target.value) loadImportBatches(e.target.value); }}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Select a reader...</option>
            {allReaders.map(r => (
              <option key={r.id} value={r.id}>{r.name || r.emailCorrespondence || r.id}</option>
            ))}
          </select>
        </div>
      )}

      {/* Platform selector */}
      {!csvData && !result && (readerId || !isAdmin) && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2">Select your platform (or just upload — we'll auto-detect):</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {PLATFORMS.map(key => {
              const info = PLATFORM_INFO[key];
              const isSelected = selectedPlatform === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedPlatform(isSelected ? null : key)}
                  className={`flex items-center gap-2 rounded-lg border p-2.5 text-left transition-all ${
                    isSelected
                      ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-300'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <BookOpen className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-teal-600' : 'text-slate-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>{info.name}</p>
                  </div>
                  <ExportInstructions platform={key} />
                </button>
              );
            })}
          </div>

          <div className="mt-3">
            <input type="file" accept=".csv,.txt" ref={fileRef} onChange={handleUpload} className="hidden" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={isAdmin && !adminReaderId}
            >
              <Upload className="h-4 w-4 mr-1" /> Upload CSV
            </Button>
            {isAdmin && !adminReaderId && (
              <p className="text-[10px] text-red-500 mt-1">Select a reader first</p>
            )}
          </div>

          {!isAdmin && (
            <div className="mt-3 text-[10px] text-slate-400 space-y-0.5">
              <p>Earn 2 points per new book added to the database</p>
              <p>Earn 5 points when your tags help verify a book (3+ readers agree)</p>
            </div>
          )}
        </div>
      )}

      {/* Unknown format error */}
      {csvData && detectedFormat === 'unknown' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700 font-semibold">Unrecognised CSV format</p>
          <p className="text-xs text-red-600 mb-2">
            We couldn't detect the platform. Supported: Goodreads, StoryGraph, LibraryThing, Kindle, BookBub, Literal.club, Oku.
          </p>
          <Button variant="outline" size="sm" onClick={reset}>Try Again</Button>
        </div>
      )}

      {/* Preview */}
      {csvPreview && detectedFormat !== 'unknown' && !result && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-bold text-slate-800 mb-1">
            Detected: <span className="text-teal-600">{PLATFORM_INFO[detectedFormat]?.name || detectedFormat}</span> export
          </p>
          <div className="grid grid-cols-3 gap-2 my-3">
            <div className="rounded-lg bg-teal-50 p-2 text-center">
              <p className="text-xl font-bold text-teal-700">{csvPreview.newBooks}</p>
              <p className="text-[9px] text-teal-600 uppercase font-semibold">New books</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 text-center">
              <p className="text-xl font-bold text-slate-600">{csvPreview.matched}</p>
              <p className="text-[9px] text-slate-500 uppercase font-semibold">Already in DB</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 text-center">
              <p className="text-xl font-bold text-slate-400">{csvPreview.skipped}</p>
              <p className="text-[9px] text-slate-400 uppercase font-semibold">Skipped</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {isAdmin
              ? `Books will be saved to the shared database and this reader's personal reading history.`
              : `Your tags will be added as community submissions. You'll earn points for new books and verified tags.`
            }
          </p>
          <div className="flex gap-2">
            <Button onClick={handleImport} disabled={importing}>
              {importing ? 'Importing...' : `Import ${csvPreview.total - csvPreview.skipped} Books`}
            </Button>
            <Button variant="outline" onClick={reset}>Cancel</Button>
          </div>
          {importing && (
            <div className="mt-3">
              <Progress value={importProgress} />
              <p className="text-[10px] text-slate-400 mt-1 text-center">{importProgress}% complete</p>
            </div>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
          <p className="text-sm font-bold text-green-800 mb-1">Import Complete!</p>
          <p className="text-xs text-green-700 font-semibold">{result.shelfWrites} books added to your shelf</p>
          <p className="text-xs text-green-700">{result.added} new books added to database</p>
          <p className="text-xs text-green-700">{result.matched} matched existing records</p>
          {result.errors > 0 && <p className="text-xs text-red-600">{result.errors} skipped (no title found)</p>}
          {result.pointsEarned > 0 && (
            <p className="text-sm font-bold text-teal-700 mt-2">+{result.pointsEarned} points earned!</p>
          )}
          <Button variant="outline" size="sm" className="mt-3" onClick={reset}>Import Another</Button>
        </div>
      )}

      {/* Import history (batches) */}
      {readerId && importBatches.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <button
            onClick={() => setShowBatches(!showBatches)}
            className="flex w-full items-center justify-between text-sm font-bold text-slate-700"
          >
            <span>Import History ({importBatches.length})</span>
            {showBatches ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showBatches && (
            <div className="mt-3 space-y-2">
              {importBatches.map(batch => (
                <div key={batch.batchId} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                  <div>
                    <p className="text-xs font-semibold text-slate-700">
                      {PLATFORM_INFO[batch.platform]?.name || batch.platform} import
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {batch.booksImported} books ({batch.newBooksAdded} new) ·{' '}
                      {batch.importedAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                    </p>
                    {batch.pointsEarned > 0 && (
                      <p className="text-[10px] text-teal-600 font-semibold">+{batch.pointsEarned} points</p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBatch(batch)}
                      disabled={deletingBatch === batch.batchId}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      {deletingBatch === batch.batchId ? '...' : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
